import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShieldCheck, ShieldX, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  listDeviations,
  listPendingDeviations,
  supervisorDecideDeviation,
  officeDecideDeviation,
} from '../lib/api';

const statusColor = {
  PENDIENTE: 'bg-slate-600',
  APROBADA_SUPERVISOR: 'bg-amber-600',
  APROBADA_OFICINA: 'bg-amber-600',
  APROBADA_COMPLETA: 'bg-emerald-600',
  RECHAZADA: 'bg-red-600',
  EXPIRADA: 'bg-slate-400',
};

export default function DeviationsPage() {
  const { user, isAdmin } = useAuth();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        listPendingDeviations(),
        listDeviations({ limit: 50 }),
      ]);
      setPending(p.data || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error('Error cargando autorizaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (deviation, role, decision) => {
    try {
      const data = { decision, notes: notes[deviation.id] || null };
      if (role === 'supervisor') {
        await supervisorDecideDeviation(deviation.id, data);
      } else {
        await officeDecideDeviation(deviation.id, data);
      }
      toast.success(`Decisión registrada: ${decision}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error registrando decisión');
    }
  };

  const canDecideAsSupervisor = (d) =>
    !d.supervisor_decision && (user?.role === 'supervisor' || isAdmin);
  const canDecideAsOffice = (d) =>
    !d.office_decision && isAdmin;

  const renderCard = (d, showActions = true) => (
    <Card key={d.id} data-testid={`deviation-card-${d.id}`} className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <p className="font-semibold text-slate-900">{d.user_name || d.user_id}</p>
            <p className="text-xs text-slate-500">{d.user_phone}</p>
          </div>
          <Badge className={statusColor[d.status]} data-testid={`deviation-status-${d.id}`}>
            {d.status}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div>
            <span className="text-slate-500">Tienda solicitada:</span>
            <p className="font-medium">{d.requested_store_name || 'Fuera de ruta'}</p>
          </div>
          <div>
            <span className="text-slate-500">Tipo:</span>
            <p className="font-medium">{d.deviation_type}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500">Motivo:</span>
            <p className="text-slate-700">{d.reason}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs border-t pt-2 mt-2">
          <div className="flex items-center gap-1">
            {d.supervisor_decision === 'APROBADO' && <Badge className="bg-emerald-600 text-[10px]">SUP ✓</Badge>}
            {d.supervisor_decision === 'RECHAZADO' && <Badge className="bg-red-600 text-[10px]">SUP ✗</Badge>}
            {!d.supervisor_decision && <Badge variant="outline" className="text-[10px]">SUP pendiente</Badge>}
            {d.supervisor_notes && <span className="text-slate-500 truncate">{d.supervisor_notes}</span>}
          </div>
          <div className="flex items-center gap-1">
            {d.office_decision === 'APROBADO' && <Badge className="bg-emerald-600 text-[10px]">OFI ✓</Badge>}
            {d.office_decision === 'RECHAZADO' && <Badge className="bg-red-600 text-[10px]">OFI ✗</Badge>}
            {!d.office_decision && <Badge variant="outline" className="text-[10px]">OFI pendiente</Badge>}
            {d.office_notes && <span className="text-slate-500 truncate">{d.office_notes}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-2">
          <Clock className="w-3 h-3" />
          <span>Creada: {new Date(d.created_at).toLocaleString()}</span>
        </div>
        {showActions && (canDecideAsSupervisor(d) || canDecideAsOffice(d)) && (
          <div className="mt-3 border-t pt-3 space-y-2">
            <Label className="text-xs">Notas (opcional)</Label>
            <Input
              value={notes[d.id] || ''}
              onChange={(e) => setNotes({ ...notes, [d.id]: e.target.value })}
              placeholder="Justificación..."
              data-testid={`deviation-notes-${d.id}`}
            />
            <div className="flex flex-wrap gap-2">
              {canDecideAsSupervisor(d) && (
                <>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => decide(d, 'supervisor', 'APROBADO')}
                    data-testid={`approve-sup-${d.id}`}>
                    <ShieldCheck className="w-3 h-3 mr-1" /> Aprobar (Supervisor)
                  </Button>
                  <Button size="sm" variant="destructive"
                    onClick={() => decide(d, 'supervisor', 'RECHAZADO')}
                    data-testid={`reject-sup-${d.id}`}>
                    <ShieldX className="w-3 h-3 mr-1" /> Rechazar (Sup)
                  </Button>
                </>
              )}
              {canDecideAsOffice(d) && (
                <>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => decide(d, 'office', 'APROBADO')}
                    data-testid={`approve-off-${d.id}`}>
                    <ShieldCheck className="w-3 h-3 mr-1" /> Aprobar (Oficina)
                  </Button>
                  <Button size="sm" variant="destructive"
                    onClick={() => decide(d, 'office', 'RECHAZADO')}
                    data-testid={`reject-off-${d.id}`}>
                    <ShieldX className="w-3 h-3 mr-1" /> Rechazar (Oficina)
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="deviations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Autorizaciones de Desvío</h1>
          <p className="text-slate-500">Aprobación doble: Supervisor + Oficina Central</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-deviations">
          <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
        </Button>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pendientes ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 mt-4">
          {loading ? (
            <Skeleton className="h-40" />
          ) : pending.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">No hay solicitudes pendientes.</CardContent></Card>
          ) : pending.map((d) => renderCard(d, true))}
        </TabsContent>

        <TabsContent value="history" className="space-y-3 mt-4">
          {loading ? (
            <Skeleton className="h-40" />
          ) : history.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">Sin historial.</CardContent></Card>
          ) : history.map((d) => renderCard(d, false))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
